import FileType from "@/models/enums/file-type";
import FileData2D from "./models/file-data-2d";
import ProjectOrganisation from "./models/project-organisation";
import ProjectOrganisationStorey from "./models/project-organisation-storey";
import ProjectOrganisationReducerAction from "./project-organisation-reducer-action";
import FileData3D from "./models/file-data-3d";
import ProjectOrganisationBuilding from "./models/project-organisation-building";
import BaseFileData from "./models/base-file-data";
import { applySetupFiles, calculateStoreyIndexes } from "./project-organisation-helper";

const projectOrganisationReducer: (
    draft: ProjectOrganisation,
    action: ProjectOrganisationReducerAction
) => void = (draft, action) => {
    switch (action.type) {

        case 'populate': {
            Object.assign(draft, {
                buildings: action.payload.buildings,
                files2d: action.payload.files2d,
                files3d: action.payload.files3d,
                isDirty: false
            });
            break;
        }

        case 'hydrate': {

            const { payload } = action;

            const hydratedFiles2d = (payload.files2d ?? []).map(f => new FileData2D(f.id, f.name, FileType.File2D, f.revision, f.relativePath, f.fileMetaObject, f.content));
            const hydratedFiles3d = (payload.files3d ?? []).map(f => new FileData3D(f.id, f.name, FileType.File3D, f.revision, f.relativePath, f.fileMetaObject, f.content));

            const hydratedBuildings = (payload.buildings ?? []).map(b => {
                const building = new ProjectOrganisationBuilding(b.id, b.name, b.index);

                (b.files2d ?? []).forEach(f => {
                    building.files2d.push(new FileData2D(f.id, f.name, FileType.File2D, f.revision, f.relativePath, f.fileMetaObject, f.content));
                });

                (b.files3d ?? []).forEach(f => {
                    if (f.fileType === FileType.File3DFrom2D) {
                        building.file3dFrom2d = new BaseFileData(f.id, f.name, FileType.File3DFrom2D, f.revision, f.relativePath, f.fileMetaObject, f.content);
                    } else {
                        building.files3d.push(new FileData3D(f.id, f.name, FileType.File3D, f.revision, f.relativePath, f.fileMetaObject, f.content));
                    }
                });

                building.storeys = (b.storeys ?? []).map(s => {
                    const storey = new ProjectOrganisationStorey(s.id, s.name, s.altimetry, s.storeyType, s.index);
                    (s.files2d ?? []).forEach(f => {
                        storey.files2d.push(new FileData2D(f.id, f.name, FileType.File2D, f.revision, f.relativePath, f.fileMetaObject, f.content));
                    });
                    (s.files3d ?? []).forEach(f => {
                        storey.files3d.push(new FileData3D(f.id, f.name, FileType.File3D, f.revision, f.relativePath, f.fileMetaObject, f.content));
                    });
                    return storey;
                });

                building.storeys.sort((a, b2) => b2.altimetry - a.altimetry);
                return building;
            });

            Object.assign(draft, {
                buildings: hydratedBuildings,
                files2d: hydratedFiles2d,
                files3d: hydratedFiles3d,
                isDirty: false,
            });

            break;
        }

        case 'addBuilding': {
            draft.addBuilding(action.name);
            draft.buildings.forEach((building, index) => {
                const targetBuilding = draft.getBuilding(building.id);
                if (targetBuilding)
                    targetBuilding.index = index + 1;
            });
            draft.setDirtyFlag(true);
            break;
        }

        case 'editBuilding': {
            const targetBuilding = draft.getBuilding(action.buildingId);
            if (targetBuilding) {
                targetBuilding.handeNewBuildingName(action.name);
                draft.setDirtyFlag(true);
            }
            break;
        }

        case 'removeBuilding': {
            draft.removeBuilding(action.buildingId);
            draft.buildings.forEach((building, index) => {
                const targetBuilding = draft.getBuilding(building.id);
                if (targetBuilding)
                    targetBuilding.index = index + 1;
            });
            draft.setDirtyFlag(true);
            break;
        }

        case 'addStorey': {
            const targetBuilding = draft.getBuilding(action.buildingId);
            if (targetBuilding) {
                targetBuilding.addStorey(action.name, action.altimetry, action.storeyType);
                const updates = calculateStoreyIndexes(targetBuilding.storeys);
                updates.forEach(update => {
                    const storey = targetBuilding.storeys.find(s => s.id === update.id);
                    if (storey) {
                        storey.index = update.index;
                    }
                });
                draft.setDirtyFlag(true);
            }
            break;
        }

        case 'editStorey': {
            const targetBuilding = draft.getBuilding(action.buildingId);
            const targetStorey = targetBuilding?.getStorey(action.storeyId);
            if (targetBuilding && targetStorey) {
                let hasChanged = false;
                if (action.name) {
                    targetStorey.name = action.name;
                    hasChanged = true;
                }

                if (action.altimetry != null) {
                    targetStorey.altimetry = action.altimetry;
                    targetBuilding.storeys.sort((a, b) => b.altimetry - a.altimetry);
                    const updates = calculateStoreyIndexes(targetBuilding.storeys);
                    updates.forEach(update => {
                        const storey = targetBuilding.storeys.find(s => s.id === update.id);
                        if (storey) {
                            storey.index = update.index;
                        }
                    });
                    hasChanged = true;
                }

                if (action.storeyType != null) {
                    targetStorey.storeyType = action.storeyType;
                    hasChanged = true;
                }

                if (hasChanged) {
                    draft.setDirtyFlag(true);
                }
            }
            break;
        }

        case 'removeStorey': {
            const targetBuilding = draft.getBuilding(action.buildingId);
            if (targetBuilding) {
                targetBuilding.removeStorey(action.storeyId);
                const updates = calculateStoreyIndexes(targetBuilding.storeys);
                updates.forEach(update => {
                    const storey = targetBuilding.storeys.find(s => s.id === update.id);
                    if (storey) {
                        storey.index = update.index;
                    }
                });
                draft.setDirtyFlag(true);
            }
            break;
        }

        case 'appendRoofs': {
            draft.buildings
                .filter(b => b.storeys.find(s => s.storeyType === "TOIT") == null)
                .forEach(building => {
                    const maxAlt = building.storeys.length > 0
                        ? building.storeys.reduce((max, storey) => Math.max(max, storey.altimetry), building.storeys[0].altimetry)
                        : 0;
                    const altimetry = Math.round(((building.storeys.length > 0 ? maxAlt : 0) + 2.7) * 100) / 100;
                    building.addStorey('Toiture', altimetry, 'TOIT');
                    const updates = calculateStoreyIndexes(building.storeys);
                    updates.forEach(update => {
                        const storey = building.storeys.find(s => s.id === update.id);
                        if (storey) {
                            storey.index = update.index;
                        }
                    });
                });
            draft.setDirtyFlag(true);
            break;
        }

        case 'setupFiles': {
            applySetupFiles(draft, action.filesData, action.flowMode);
            break;
        }

        case 'addFile': {
            if (action.scope === 'project')
                draft.addFile(action.fileType, action.fileId, action.name, action.revision, action.relativePath, action.fileMetaObject);
            else if (action.scope === 'building' && action.buildingId) {
                const targetBuilding = draft.getBuilding(action.buildingId);
                targetBuilding?.addFile(action.fileType, action.fileId, action.name, action.revision, action.relativePath, action.fileMetaObject);
            } else if (action.scope === 'storey' && action.buildingId && action.storeyId) {
                const targetStorey = draft.getStoreyBuilding(action.buildingId, action.storeyId);
                targetStorey?.addFile(action.fileType, action.fileId, action.name, action.revision, action.relativePath, action.fileMetaObject);
            }

            draft.setDirtyFlag(true);
            break;
        }        

        case 'removeFile': {
            if (action.scope === 'project')
                draft.removeFile(action.fileType, action.fileId);
            else if (action.scope === 'building' && action.buildingId) {
                const targetBuilding = draft.getBuilding(action.buildingId);
                targetBuilding?.removeFile(action.fileType, action.fileId);
            } else if (action.scope === 'storey' && action.buildingId && action.storeyId) {
                const targetStorey = draft.getStoreyBuilding(action.buildingId, action.storeyId);
                targetStorey?.removeFile(action.fileType, action.fileId);
            }

            draft.setDirtyFlag(true);
            break;
        }

        case 'updatePlanFileData': {
            const found = draft.findBuildingStoreyAndFileFromFileId(action.fileId);

            if (found) {
                found.file.origin = action.fileData.origin;
                found.file.origins = action.fileData.origins;
                found.file.content = action.fileData.content;
            }

            break;
        }

        case 'setDirty': {
            draft.setDirtyFlag(true);
            break;
        }

        case 'resetDirty': {
            draft.setDirtyFlag(false);
            break;
        }

        default: {
            throw Error(`Unknown action`);
        }
    }
};

export default projectOrganisationReducer;